import { useAnalytics } from '@/services/analytics';
import { useSetUserFlags } from '@/features/user-flags/hooks';
import { servicesApi } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useSnackbars, useModals } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { atom } from 'nanostores';
import { useState } from 'react';
import { sentryCaptureException } from '@/utils/sentry';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { SupportFormModal } from '../form/components/support-form-modal';

const isProduction = __CIO_ENV__ === 'production';

const TOKEN_LIFETIME_REFRESH_THRESHOLD = 0.8;
const MAX_ZENDESK_WAIT_TIME = 4000;
const ZENDESK_POLL_INTERVAL = 150;
const MAX_ZENDESK_POLL_ATTEMPTS = Math.floor(MAX_ZENDESK_WAIT_TIME / ZENDESK_POLL_INTERVAL);

const _initializeStart = atom(0);
const _isInitialized = atom(false);
const _currentToken = atom<string | null>(null);
const _unreadChatCount = atom(0);
const _zendeskTokenState = atom<{
  token: string;
  isHandoverAllowed: boolean;
  tokenLifetimeMinutes: number;
  createdAt: number;
} | null>(null);

// Zendesk localizes the messenger iframe title and name attributes. Previously
// we were looking for `iframe[name="Messaging window"]`, which only works if
// your browser has English as the primary language. Instead we add a few
// heuristics to find the iframe. Zendesk will use the same text for the title
// and name attributes, so we can use that to identify the iframe. Additionally,
// we check if the iframe has a `src` attribute, which the messenger doesn't have.
function findZendeskMessenger() {
  const maybeMessenger = document.querySelector<HTMLIFrameElement>('iframe[name][title]');
  if (maybeMessenger == null) {
    return null;
  }

  const title = maybeMessenger.getAttribute('title');
  const name = maybeMessenger.getAttribute('name');
  if (title !== name) {
    return null;
  }

  if (maybeMessenger.getAttribute('src') != null) {
    return null;
  }

  return maybeMessenger;
}

/**
 * This function handles the zendesk token
 *
 * We have three scenarios:
 * 1. No token exists: Fetch a new token from the API along with its configuration.
 * 2. Token exists but is close to expiration: Refresh the token using the API.
 * 3. Token exists and is still valid: Use the existing token.
 */
async function getZendeskAccess(
  accountId: number,
  userId: number,
): Promise<{
  token: string;
  isHandoverAllowed: boolean;
  tokenLifetimeMinutes: number;
  createdAt: number;
}> {
  const zendeskTokenState = _zendeskTokenState.get();
  // If no token exists, fetch a new one
  if (zendeskTokenState == null) {
    const response = await servicesApi.getZendeskChatAccessToken({
      params: { accountId, userId },
    });

    const newState = {
      token: response.token,
      isHandoverAllowed: response.is_handover_allowed,
      tokenLifetimeMinutes: response.token_lifetime_minutes,
      createdAt: Date.now(),
    };

    _zendeskTokenState.set(newState);

    return newState;
  }

  // If the token is close to expiration, refresh it
  const msSinceCreation = Date.now() - zendeskTokenState.createdAt;
  if (msSinceCreation >= zendeskTokenState.tokenLifetimeMinutes * 60 * 1000 * TOKEN_LIFETIME_REFRESH_THRESHOLD) {
    const response = await servicesApi.refreshZendeskChatToken({
      params: { accountId, userId },
      body: undefined,
    });

    const newState = {
      token: response.token,
      isHandoverAllowed: zendeskTokenState.isHandoverAllowed,
      tokenLifetimeMinutes: zendeskTokenState.tokenLifetimeMinutes,
      createdAt: Date.now(),
    };

    _zendeskTokenState.set(newState);

    return newState;
  }

  // If the token is still valid, return it
  return zendeskTokenState;
}

/**
 * A react query that connects the zendesk chat widget to the user
 * and sets up the necessary event listeners.
 */
let checkForZendeskWidgetInterval: number | undefined = undefined;
function useZendeskAccess() {
  const auth = useAuth();
  return useQuery({
    queryKey: ['zendeskAccess'],
    queryFn: async () => {
      const access = await getZendeskAccess(auth.account.id, auth.user.id);

      _initializeStart.set(Date.now());
      if (_isInitialized.get() === false) {
        const conversationTags: string[] = [];
        if (access.isHandoverAllowed !== true) {
          conversationTags.push('maa-ai-only');
        }

        if (isProduction === false) {
          conversationTags.push('internal-test');
        }

        // Wait for Zendesk widget to load (up to 4 seconds) to verify widget loaded successfully
        const zeLoaded = await new Promise<boolean>((resolve) => {
          let attempts = 0;
          window.clearInterval(checkForZendeskWidgetInterval);
          checkForZendeskWidgetInterval = window.setInterval(() => {
            const zeAvailable = typeof window.zE === 'function';

            if (zeAvailable === true) {
              window.clearInterval(checkForZendeskWidgetInterval);
              checkForZendeskWidgetInterval = undefined;
              resolve(true);
            } else {
              attempts += 1;
              if (attempts >= MAX_ZENDESK_POLL_ATTEMPTS) {
                window.clearInterval(checkForZendeskWidgetInterval);
                checkForZendeskWidgetInterval = undefined;
                resolve(false);
              }
            }
          }, ZENDESK_POLL_INTERVAL);
        });
        // Temporary debugging for AI-770: if zE is undefined because the Zendesk script is still loading, 'show' is skipped and _isInitialized set true (widget stays hidden)
        if (!zeLoaded) {
          sentryCaptureException(new Error('Zendesk widget (zE) was not available during initialization'), {
            level: 'warning',
            tags: { type: 'zendesk-init-debug' },
            extra: {
              totalDuration: Date.now() - _initializeStart.get(),
            },
          });
        }

        window.zE?.('messenger:set', 'conversationTags', conversationTags);
        window.zE?.('messenger:on', 'unreadMessages', (count: number) => {
          _unreadChatCount.set(count);
        });
        window.zE?.('messenger', 'show');
        _isInitialized.set(true);

        return access;
      }

      return access;
    },
    enabled: auth.account.is_masq === false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
    staleTime: Infinity,
  });
}

export function useSupportChatSparkles() {
  const auth = useAuth();
  const { mutate: mutateUserFlags } = useSetUserFlags();
  const [shouldShowSparkle, setShowSparkle] = useState(auth.user.user_flags?.show_chat_sparkles ?? false);

  const disableSparkle = () => {
    if (shouldShowSparkle !== true) {
      return;
    }

    setShowSparkle(false);
    mutateUserFlags({
      accountId: auth.account.id,
      userId: auth.user.id,
      flags: {
        show_chat_sparkles: false,
      },
    });
  };

  return { shouldShowSparkle, disableSparkle };
}

let draftMessageInterval: number | undefined = undefined;
let checkForZendeskInterval: number | undefined = undefined;
export function useSupportChat() {
  const auth = useAuth();
  const permissions = usePermissions();
  const modals = useModals();
  const { data, refetch, isFetching } = useZendeskAccess();
  const unreadCount = useStore(_unreadChatCount);
  const { track } = useAnalytics();
  const workspace = useCurrentWorkspace();
  const snackbars = useSnackbars();

  async function openChat(message?: string): Promise<boolean> {
    const errorHint = { tags: { type: 'support-chat-open-failed' } };

    async function doOpening(): Promise<boolean> {
      try {
        const response = await refetch();
        if (response.data == null) {
          sentryCaptureException(
            new Error('Support chat failed to open: zendesk token request returned null data'),
            errorHint,
          );
          snackbars.create({
            message: 'Unable to open support chat. Please try again.',
            variant: 'error',
          });
          return false;
        }

        const { token } = response.data;

        const zE = window.zE;
        // If widget still isn't available, fall back to inline support form
        if (zE == null) {
          sentryCaptureException(
            new Error('Support chat failed to open: Zendesk widget (window.zE) is not available'),
            {
              ...errorHint,
              extra: {
                totalDuration: Date.now() - _initializeStart.get(),
              },
            },
          );

          snackbars.create({
            message: 'Chat widget failed to load. Opening support form instead.',
            variant: 'error',
          });

          openForm(message);
          return true;
        }

        // when the token is refreshed, we need to re-login the user in the chat widget
        if (_currentToken.get() !== token) {
          await new Promise<void>((resolve) => {
            zE('messenger', 'loginUser', (callback: (token: string) => void) => {
              callback(token);
              _currentToken.set(token);
              resolve();
            });
          });
        }

        // AI-770: it's possible that the Zendesk window is still hidden due to the script being loaded asynchronously.
        zE('messenger', 'show');
        zE('messenger', 'open');
        _unreadChatCount.set(0);

        // there is no way to set a draft of a message in the Zendesk chat widget,
        // so we need to set the message in the textarea directly
        // this is a hacky way to do it, but it works
        // we wait for the iframe to be loaded and then set the value of the textarea
        if (message != null) {
          let attempts = 0;
          window.clearInterval(draftMessageInterval);
          draftMessageInterval = window.setInterval(() => {
            // this is convoluted, but we need to set the value in this
            // specific way for zendesk to recognize it as user input
            const iframe = findZendeskMessenger();
            const textarea = iframe?.contentDocument?.querySelector('textarea');

            if (textarea != null) {
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLTextAreaElement.prototype,
                'value',
              )?.set;

              nativeInputValueSetter?.call(textarea, message);

              const event = new Event('input', { bubbles: true });
              textarea.dispatchEvent(event);
              window.clearInterval(draftMessageInterval);
              draftMessageInterval = undefined;
            } else {
              attempts += 1;
              if (attempts >= MAX_ZENDESK_POLL_ATTEMPTS) {
                window.clearInterval(draftMessageInterval);
                draftMessageInterval = undefined;
                // eslint-disable-next-line no-console
                console.warn('Failed to set message in support chat after 4 seconds');
              }
            }
          }, ZENDESK_POLL_INTERVAL);
        }

        // Wait for Zendesk iframe to load (up to 4 seconds) to verify widget opened successfully
        const iframeLoadStart = Date.now();
        const iframeLoaded = await new Promise<boolean>((resolve) => {
          let attempts = 0;
          window.clearInterval(checkForZendeskInterval);
          checkForZendeskInterval = window.setInterval(() => {
            const iframe = findZendeskMessenger();
            const textarea = iframe?.contentDocument?.querySelector('textarea');

            if (textarea != null) {
              window.clearInterval(checkForZendeskInterval);
              checkForZendeskInterval = undefined;
              resolve(true);
            } else {
              attempts += 1;
              if (attempts >= MAX_ZENDESK_POLL_ATTEMPTS) {
                window.clearInterval(checkForZendeskInterval);
                checkForZendeskInterval = undefined;
                resolve(false);
              }
            }
          }, ZENDESK_POLL_INTERVAL);
        });

        // If iframe didn't load, hide the widget and fall back to support form
        if (!iframeLoaded) {
          const iframeHTML = findZendeskMessenger();

          zE('messenger', 'hide');
          sentryCaptureException(
            new Error('Support chat failed to open: Could not find Zendesk chat textarea iframe'),
            {
              ...errorHint,
              extra: {
                textAreaCount: iframeHTML?.contentDocument?.querySelectorAll('textarea').length ?? 0,
                inputCount: iframeHTML?.contentDocument?.querySelectorAll('input').length ?? 0,
                hasLog: (iframeHTML?.contentDocument?.querySelectorAll('[role="log"]').length ?? 0) > 0,
                totalDuration: Date.now() - _initializeStart.get(),
                iframeLoadStart: iframeLoadStart - _initializeStart.get(),
                iframeLoadDuration: Date.now() - iframeLoadStart,
              },
            },
          );
          snackbars.create({
            message: 'Chat widget failed to load. Opening support form instead.',
            variant: 'error',
          });

          openForm(message);
          return true;
        }

        return true;
      } catch (error) {
        sentryCaptureException(error, errorHint);
        snackbars.create({
          message: 'Unable to open support chat. Please try again.',
          variant: 'error',
        });
        return false;
      }
    }

    // show a warning to our developers about touching production Zendesk data
    if (!isProduction) {
      snackbars.create({
        message:
          'Support chat uses production Zendesk account, but you are in development mode. If you continue, you will mess with production data and our stats. Please proceed with caution.',
        action: {
          label: 'Continue',
          onClick: () => {
            void doOpening();
          },
        },
      });
      return true;
    } else {
      return await doOpening();
    }
  }

  function openForm(initialProblem?: string, source?: string): string {
    const trackingProperties = {
      'Account Id': auth.account.id,
      'Requester Name': `${auth.user.first_name} ${auth.user.last_name}`,
      'Requester Email': auth.user.email,
      'Requester User Id': auth.user.id,
    };

    void track('inline_support', 'opened', trackingProperties);

    return modals.open(SupportFormModal, {
      accountId: auth.account.id,
      workspaceId: workspace?.id,
      userId: auth.user.id,
      userEmail: auth.user.email,
      supportTeamAccessEnabled: auth.account.support_team_access_enabled ?? false,
      accessGrantedUntil: auth.account.access_granted_until,
      hasEditPermission: permissions.hasPermission('account/support_access/edit'),
      initialProblem,
      source,
      trackingProperties,
    });
  }

  return {
    openChat,
    openForm,
    isLoading: isFetching,
    unreadCount,
    isVisible: data != null,
    isHandoverAllowed: data?.isHandoverAllowed === true,
  };
}
